import { useMemo } from 'react';
import { CellType } from '@grapecity/wijmo.grid';
import { FlexGridFilter } from '@grapecity/wijmo.grid.filter';
import CustomGrid from 'components/grids/CustomGrid';
import { useMechUtTypes } from 'hooks/useMechUtTypes';
import { usePrdnProcOrder } from 'hooks/usePrdnProcOrder';
import { useUtComparisonStore } from '../store/useUtComparisonStore';
import { AnalysisRow, UtCellData } from '../types';
import { fmt } from '../utils/excelUtils';
import { sortAnalysisRows } from '../utils/sortRows';
import SectionBox from './SectionBox';

/** 그리드 본문 높이 (기존 .table-outer의 max-height와 같은 값) */
const GRID_HEIGHT = 480;

/** 고정열 5개의 바인딩 키 — 모드에 따라 담기는 값만 바뀐다 */
const FIXED_KEYS = ['f0', 'f1', 'f2', 'f3', 'f4'] as const;

/** 행 전체 강조 여부를 담는 키 (컬럼으로는 쓰지 않는다) */
const ROW_DIFF_KEY = '__rowDiff';

/** UT 셀 한 칸의 표시 규칙 — itemFormatter가 이걸 보고 텍스트·색을 만든다 */
interface ColMeta {
  /** 소수 자릿수 (fmt의 d) */
  digits: number;
  /** 차이 칸 — 양수면 '+' 접두, 부호에 따라 색이 붙는다 */
  signed?: boolean;
  /** 단위사용량처럼 톤을 낮추는 칸 */
  muted?: boolean;
}

/**
 * N-way(버전 N개) UT 셀 서브 컬럼 정의 생성.
 *
 * 라벨과 표시 규칙을 한 번에 만든다 — 둘이 어긋나면 값과 서식이 따로 놀기 때문이다.
 * 반환 순서가 곧 컬럼 순서이고, 인덱스가 바인딩 키(`{utKey}_{idx}`)의 뒷자리다.
 */
function buildSubColumns(n: number, isPerLineMode: boolean): { label: string; meta: ColMeta }[] {
  const vNums = Array.from({ length: n }, (_, i) => i + 1);
  if (isPerLineMode) {
    return [
      ...vNums.map((v) => ({ label: `V${v} 사용량/라인`, meta: { digits: 2 } })),
      { label: '사용량 차이', meta: { digits: 2, signed: true } },
      ...vNums.map((v) => ({ label: `V${v} Q'ty/라인`, meta: { digits: 2 } })),
      { label: "Q'ty 차이", meta: { digits: 2, signed: true } },
    ];
  }
  return [
    ...vNums.map((v) => ({ label: `V${v} 총사용량`, meta: { digits: 2 } })),
    { label: '총사용량 차이', meta: { digits: 2, signed: true } },
    ...vNums.map((v) => ({ label: `V${v} 단위사용량`, meta: { digits: 2, muted: true } })),
    { label: '단위사용량 차이', meta: { digits: 2, signed: true } },
    ...vNums.map((v) => ({ label: `V${v} Q'ty`, meta: { digits: 0 } })),
    { label: "Q'ty 차이", meta: { digits: 0, signed: true } },
  ];
}

/** UT 셀 한 개 → 서브 컬럼 순서대로 펼친 숫자 배열 (buildSubColumns와 순서가 같아야 한다) */
function flattenUtCell(d: UtCellData | undefined, n: number, isPerLineMode: boolean): number[] {
  const at = (arr: number[] | undefined, i: number) => arr?.[i] ?? 0;
  const usage = Array.from({ length: n }, (_, i) => at(d?.usage, i));
  const qty = Array.from({ length: n }, (_, i) => at(d?.qty, i));
  if (isPerLineMode) {
    return [...usage, d?.diffU ?? 0, ...qty, d?.diffQ ?? 0];
  }
  const unit = Array.from({ length: n }, (_, i) => at(d?.unit, i));
  // 단위사용량 차이는 저장돼 있지 않아 첫 버전과 마지막 버전의 차로 계산한다 (기존 표와 동일)
  const diffUnit = unit.length > 0 ? unit[unit.length - 1] - unit[0] : 0;
  return [...usage, d?.diffU ?? 0, ...unit, diffUnit, ...qty, d?.diffQ ?? 0];
}

/**
 * ④ Machine/Line별 UT 사용량 비교 분석 표.
 *
 * [26.09.23] 직접 만든 <table>에서 공용 CustomGrid(Wijmo)로 옮겼다.
 * - 행 가상화 — 수천 행이어도 보이는 만큼만 DOM에 만든다. 예전 마크업은 행마다 고정열 5칸이
 *   position:sticky라 스크롤·드래그가 통째로 느려졌다.
 * - 좌측 5열 고정은 frozenColumns가 맡는다 (예전엔 left: 0/60/130/200/280px 하드코딩이라
 *   열 너비가 바뀌면 어긋났다).
 * - 정렬·칼럼 필터는 그리드 기본 기능(FlexGridFilter)을 쓴다. 그래서 스토어의
 *   sortConfig/columnFilters와 자체 FilterDropdown은 더 이상 쓰지 않는다.
 *
 * ⚠ 값은 숫자 그대로 바인딩하고 표시 문자열은 itemFormatter에서 만든다 —
 *   문자열을 바인딩하면 정렬이 사전순이 돼 버린다.
 */
const AnalysisTable = () => {
  const analysisResult = useUtComparisonStore((s) => s.analysisResult);
  const isPerLineMode = useUtComparisonStore((s) => s.isPerLineMode);
  const versionCount = useUtComparisonStore((s) => s.versionLabels.length);
  const currentUT = useUtComparisonStore((s) => s.currentUT);
  const diffOnly = useUtComparisonStore((s) => s.diffOnly);
  const utTypes = useMechUtTypes();
  /** 공정 표시 순서 — 이름순이 아니라 공통코드(PRDN_PROC_CD) 등록 순서를 따른다 */
  const procOrder = usePrdnProcOrder();

  const n = Math.max(versionCount, 2);
  const utList = useMemo(
    () => (currentUT === 'ALL' ? utTypes : utTypes.filter((u) => u.key === currentUT)),
    [utTypes, currentUT]
  );

  /**
   * 화면에 그릴 행 — 표시 옵션(차이만/UT 항목)을 적용하고 기본 순서로 정렬한다.
   * 칼럼 헤더 정렬·필터는 그리드(FlexGridFilter)가 맡으므로 여기서는 기본 순서만 잡는다.
   */
  const rows = useMemo(() => {
    if (!analysisResult) return [] as AnalysisRow[];
    let list = analysisResult;
    // "차이 있는 항목만 표시" — Machine(+Main) 전체 합산 기준으로 변화 없는 행은 제거
    if (diffOnly) list = list.filter((r) => r.machineHasDiff);
    if (currentUT !== 'ALL') {
      list = list.filter((r) => r.ut[currentUT] && r.ut[currentUT].diffU !== 0);
    }
    return sortAnalysisRows(list, procOrder, isPerLineMode);
  }, [analysisResult, diffOnly, currentUT, procOrder, isPerLineMode]);

  const rowHasDiff = (row: AnalysisRow) =>
    currentUT === 'ALL' ? row.machineHasDiff : !!row.ut[currentUT] && row.ut[currentUT].diffU !== 0;

  const { layoutDefinition, colMeta } = useMemo(() => {
    const fixedLabels = isPerLineMode
      ? ['공정', '설비군', '세부공정', 'Main', 'Machine']
      : ['공정', '설비군', '세부공정', 'Machine', '라인'];
    const fixedWidths = [110, 120, 120, 150, 110];
    const subColumns = buildSubColumns(n, isPerLineMode);
    const meta: Record<string, ColMeta> = {};

    const layout: any[] = FIXED_KEYS.map((key, i) => ({
      binding: key,
      header: fixedLabels[i],
      minWidth: 70,
      width: fixedWidths[i],
    }));

    utList.forEach((ut) => {
      layout.push({
        header: ut.label,
        columns: subColumns.map((sub, idx) => {
          const binding = `${ut.key}_${idx}`;
          meta[binding] = sub.meta;
          return { binding, header: sub.label, minWidth: 70, width: 100, align: 'right' };
        }),
      });
    });

    return { layoutDefinition: layout, colMeta: meta };
  }, [utList, n, isPerLineMode]);

  /** AnalysisRow → 그리드가 읽을 평평한 객체 */
  const rowData = useMemo(
    () =>
      rows.map((row) => {
        const item: Record<string, any> = {
          f0: row.process,
          f1: row.eqGroup,
          f2: row.subProcess,
          f3: isPerLineMode ? row.main : row.machine,
          f4: isPerLineMode ? row.machine : row.line,
          [ROW_DIFF_KEY]: rowHasDiff(row),
        };
        utList.forEach((ut) => {
          flattenUtCell(row.ut[ut.key], n, isPerLineMode).forEach((v, idx) => {
            item[`${ut.key}_${idx}`] = v;
          });
        });
        return item;
      }),
    [rows, utList, n, isPerLineMode, currentUT]
  );

  const onItemFormatter = (panel: any, r: number, c: number, cell: HTMLElement) => {
    if (CellType.Cell !== panel.cellType) return;
    const item = panel.rows[r]?.dataItem;
    if (!item) return;

    // 셀 엘리먼트는 재사용되므로 매번 초기화한다 — 안 그러면 이전 행의 색이 남는다
    cell.style.color = '';
    cell.style.fontWeight = '';
    cell.style.background = '';

    // 차이가 있는 행 전체 강조 (Warning-Major-50)
    if (item[ROW_DIFF_KEY]) cell.style.background = '#fff7eb';

    const meta = colMeta[panel.columns[c].binding];
    if (!meta) return; // 고정열은 텍스트 그대로 둔다

    const v = Number(item[panel.columns[c].binding] ?? 0);
    cell.textContent = (meta.signed && v > 0 ? '+' : '') + fmt(v, meta.digits);
    if (meta.signed) {
      cell.style.color = v > 0 ? 'var(--danger)' : v < 0 ? 'var(--success)' : 'var(--text-light)';
      if (v !== 0) cell.style.fontWeight = '700';
    } else if (meta.muted) {
      cell.style.color = 'var(--text-muted)';
    }
  };

  /**
   * 칼럼 필터를 직접 단다.
   *
   * CustomGrid의 기본 필터(isFilter)는 **그리드 생성 시점의 layoutDefinition**으로
   * filterColumns 목록을 한 번 만들어 FlexGridFilter에 고정한다. 이 표는 컬럼이 나중에
   * 다시 만들어지기 때문에(UT 항목 목록이 공통코드 비동기 조회 / UT 항목 선택 / 1라인별 모드)
   * 첫 렌더에는 고정 5열밖에 없고, 뒤늦게 생긴 UT 컬럼들은 그 목록에서 빠져 필터가 안 열린다.
   *
   * filterColumns를 지정하지 않으면 FlexGridFilter가 "모든 컬럼"을 대상으로 잡고
   * 필터 편집기도 컬럼별로 그때그때 만든다 — 컬럼이 바뀌어도 따라온다.
   */
  const onInitialized = (grid: any) => {
    new FlexGridFilter(grid);
  };

  if (!analysisResult) return null;

  const diffCount = rows.filter(rowHasDiff).length;

  return (
    <SectionBox
      no="④"
      title="Machine/Line별 UT 사용량 비교 분석"
      /* 총 건수는 표시 옵션을 거친 행 수, 차이 건수는 그중 차이가 있는 행 수 */
      count={rows.length}
      note={
        <>
          {/* 차이 건수는 눈에 띄게 빨간색 (sharedStyles.ts의 .section-note strong.danger) */}
          차이 <strong className="danger">{diffCount.toLocaleString()}</strong>건
        </>
      }
    >
      {isPerLineMode && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 10px' }}>
          ※ 1라인별 사용량 비교 모드: 각 버전의 라인정보에서 설비군 매칭 라인수량으로 나눈 라인당
          평균 사용량을 비교합니다.
        </p>
      )}
      {/* 헤더 글자 크기만 예전 표에 맞춰 되돌린다 — sharedStyles.ts의 .grid-sm-header 참고 */}
      <div className="grid-sm-header">
        <CustomGrid
          id="utMechAnalysisGrid"
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={GRID_HEIGHT}
          frozenColumns={5}
          itemFormatter={onItemFormatter}
          initialized={onInitialized}
          isFilter={false}
          isSelector={false}
          isReadOnly={true}
          allowPinning={false}
        />
      </div>
    </SectionBox>
  );
};

export default AnalysisTable;
